import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import { apiErrorMessage } from './apiError';
import { ImportResult, importDuplicates, savedRequestIds } from './pdsImport';

type PdsImportInput = {
  url: string;
  body: FormData;
  /** Nom du fichier importé, affiché dans le bandeau ; absent pour un copier/coller. */
  fileName?: string;
};

export type PdsImportState = {
  importPds: (input: PdsImportInput) => void;
  isImporting: boolean;
  hasFailed: boolean;
  /** Motif renvoyé par l'API, à afficher sous le libellé générique. `null` s'il est muet. */
  failureReason: string | null;
  dismissError: () => void;
};

/**
 * Import de points de service, partagé par les onglets fichier et copier/coller :
 * même requête multipart, même invalidation des listes, même bilan remonté au contexte.
 */
export function usePdsImport(): PdsImportState {
  const queryClient = useQueryClient();
  const { dispatch } = useInterventionCreation();

  const mutation = useMutation<ImportResult, unknown, PdsImportInput>({
    mutationFn: async ({ url, body }) => {
      const response = await axios.post(url, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Laisse passer le FormData tel quel, sinon axios sérialise en JSON.
        transformRequest: [(data) => data]
      });

      return response.data;
    },
    onSuccess: (importResult, input) => {
      // Le bandeau de rapport est le seul affichage du bilan : tout passe par le contexte.
      dispatch({
        type: 'reportImport',
        report: {
          fileName: input.fileName ?? null,
          addedCount: importResult.summary.saved_count,
          invalidCount: importResult.summary.invalid_count,
          duplicateCount: importResult.summary.duplicate_count,
          rejections: importResult.invalid_data ?? [],
          duplicates: importDuplicates(importResult)
        }
      });
      // Un import remplace le marquage précédent : « Nouveau » désigne toujours le dernier ajout.
      dispatch({ type: 'setNewRequests', ids: savedRequestIds(importResult) });
      // `pds/to-select/` n'est plus lu par cet écran : rien à invalider de ce côté.
      queryClient.invalidateQueries(['exocet-pds-to-qualify']);
    }
  });

  return {
    importPds: mutation.mutate,
    isImporting: mutation.isLoading,
    hasFailed: mutation.isError,
    failureReason: apiErrorMessage(mutation.error),
    dismissError: mutation.reset
  };
}
